import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRestaurantSlug } from '../hooks/useRestaurantSlug';

interface HeroNewProps {
  restaurantName: string;
  restaurantSlug: string;
  hasSlugParam: boolean;
}

const HeroNew: React.FC<HeroNewProps> = ({ restaurantName, restaurantSlug, hasSlugParam }) => {
  const [isChatRevealed, setIsChatRevealed] = useState(false);
  const voiceflowRef = useRef<HTMLDivElement>(null);
  const chatSectionRef = useRef<HTMLDivElement>(null);
  
  // Check if the slug exists in Airtable
  const { restaurantData, isLoading: isSlugLoading, error: slugError, isValidSlug, slugChecked } = useRestaurantSlug(
    hasSlugParam ? restaurantSlug : null
  );

  // Auto-reveal chat when restaurant parameter is present and valid
  useEffect(() => {
    if (hasSlugParam && slugChecked && isValidSlug && !isChatRevealed) {
      // Auto-reveal the chat for valid restaurant parameters
      setTimeout(() => {
        setIsChatRevealed(true);
        // Auto-scroll to chat section after reveal
        setTimeout(() => {
          chatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }, 1000); // Small delay to show loading animation first
    }
  }, [hasSlugParam, slugChecked, isValidSlug, isChatRevealed]);

  // Initialize Voiceflow when chat is revealed and slug is valid
  useEffect(() => {
    if (isChatRevealed && hasSlugParam && isValidSlug) {
      // Initialize Voiceflow for specific restaurant demo
      const initializeVoiceflowOnMount = () => {
        if (window.initializeVoiceflow) {
          const targetElement = document.getElementById('vf-container-hero');
          if (targetElement) {
            window.initializeVoiceflow(restaurantSlug, targetElement);
          }
        } else {
          // Retry if Voiceflow script hasn't loaded yet
          setTimeout(initializeVoiceflowOnMount, 500);
        }
      };

      // Small delay to ensure DOM is ready
      setTimeout(initializeVoiceflowOnMount, 100);
    }
  }, [restaurantSlug, hasSlugParam, isChatRevealed, isValidSlug]);

  const handleRevealChat = () => {
    setIsChatRevealed(true);
    // Smooth scroll to chat section
    setTimeout(() => {
      chatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  return (
    <section className="relative min-h-screen flex items-center section-padding py-20 overflow-hidden bg-slate-900">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-yellow-400 rounded-full filter blur-3xl"></div>
        </div>
      </div>

      <div className="max-width-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Text + CTA */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="wave-emoji inline-block">👋</span> Welcome to <span className="text-yellow-400">Kairos</span>
            </motion.h1>
            
            <motion.p 
              className="text-xl lg:text-2xl text-slate-300 mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {hasSlugParam && slugChecked && isValidSlug ? (
                <>
                  Experience a demo tailored for <span className="font-semibold text-yellow-400">{restaurantData?.name || restaurantName}</span>.
                </>
              ) : hasSlugParam && slugChecked && !isValidSlug ? (
                <>Restaurant not found. Request a personalized demo below.</>
              ) : hasSlugParam && isSlugLoading ? (
                <>Loading restaurant information...</>
              ) : (
                <>A personalized AI front desk for restaurants.</>
              )}
            </motion.p>

            <motion.button
              onClick={handleRevealChat}
              disabled={hasSlugParam && slugChecked && isValidSlug && !isChatRevealed}
              className={`group relative inline-flex items-center px-8 py-4 text-lg font-medium text-slate-900 rounded-full transition-all duration-300 shadow-lg transform ${
                hasSlugParam && slugChecked && isValidSlug && !isChatRevealed
                  ? 'bg-yellow-300 cursor-not-allowed opacity-75'
                  : 'bg-yellow-400 hover:bg-yellow-300 hover:shadow-xl hover:-translate-y-0.5'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={hasSlugParam && slugChecked && isValidSlug && !isChatRevealed ? {} : { scale: 1.05 }}
              whileTap={hasSlugParam && slugChecked && isValidSlug && !isChatRevealed ? {} : { scale: 0.95 }}
            >
              <span className="mr-2">🎯</span>
              {hasSlugParam && slugChecked && isValidSlug ? 
                (isChatRevealed ? 'Demo Active' : 'Loading Demo...') : 
                'Request Demo'
              }
              <motion.svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </motion.svg>
            </motion.button>

            {/* Trust Indicators */}
            <motion.div 
              className="mt-12 grid grid-cols-3 gap-4 lg:flex lg:items-center lg:space-x-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm px-4 py-2 rounded-full border border-slate-700">
                <div className="w-8 h-8 bg-yellow-400/20 rounded-full flex items-center justify-center">
                  <span className="text-sm">🌍</span>
                </div>
                <span className="text-sm text-slate-300 font-medium">100+ Languages</span>
              </div>
              <div className="flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm px-4 py-2 rounded-full border border-slate-700">
                <div className="w-8 h-8 bg-yellow-400/20 rounded-full flex items-center justify-center">
                  <span className="text-sm">⚡</span>
                </div>
                <span className="text-sm text-slate-300 font-medium">Instant Setup</span>
              </div>
              <div className="flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm px-4 py-2 rounded-full border border-slate-700">
                <div className="w-8 h-8 bg-yellow-400/20 rounded-full flex items-center justify-center">
                  <span className="text-sm">🎨</span>
                </div>
                <span className="text-sm text-slate-300 font-medium">White-labeled</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Embedded Voiceflow Chat */}
          <motion.div 
            ref={chatSectionRef}
            className="relative"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <AnimatePresence>
              {!isChatRevealed ? (
                <motion.div
                  key="placeholder"
                  className="bg-slate-800/90 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-lg"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                >
                  {isSlugLoading ? (
                    <div className="p-12 text-center">
                      <div className="animate-spin w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <h3 className="text-xl font-semibold text-white mb-2">Checking Restaurant...</h3>
                      <p className="text-slate-300">Please wait while we verify your restaurant.</p>
                    </div>
                  ) : hasSlugParam && slugChecked && isValidSlug && !isChatRevealed ? (
                    <div className="p-12 text-center">
                      <motion.div 
                        className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      <h3 className="text-xl font-semibold text-white mb-2">Preparing Your Demo...</h3>
                      <p className="text-slate-300">Loading personalized experience for {restaurantData?.name || restaurantName}</p>
                    </div>
                  ) : slugError ? (
                    <div className="p-12 text-center">
                      <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-red-400 text-2xl">⚠️</span>
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">Error</h3>
                      <p className="text-slate-300 mb-4">{slugError}</p>
                      <button 
                        onClick={() => window.location.reload()}
                        className="bg-yellow-400 text-slate-900 px-4 py-2 rounded-lg font-medium hover:bg-yellow-300 transition-colors"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <motion.div
                        className="absolute inset-0 bg-yellow-400/10 rounded-2xl filter blur-3xl"
                        animate={{
                          scale: [1, 1.2, 1],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                        }}
                      />
                      <div className="relative p-6">
                        <h3 className="text-2xl font-semibold text-white mb-4 text-center">
                          {hasSlugParam && slugChecked && !isValidSlug ? 'Restaurant Not Found' : 'Request a Personalized Demo'}
                        </h3>
                        <p className="text-slate-300 mb-6 text-center">
                          {hasSlugParam && slugChecked && !isValidSlug 
                            ? 'The restaurant you\'re looking for wasn\'t found. Please request a demo below and we\'ll set one up for you.'
                            : 'Get a tailored demo for your restaurant and see Kairos in action'
                          }
                        </p>
                        
                        {/* Google Form Integration */}
                        <div className="w-full h-96">
                          <iframe
                            src="https://forms.gle/dXpQwmXZC7ugSa5u7"
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            marginHeight={0}
                            marginWidth={0}
                            className="rounded-lg bg-white"
                            title="Restaurant Demo Request Form"
                          >
                            Loading form...
                          </iframe>
                        </div>
                        
                        <div className="mt-4 text-center">
                          <p className="text-sm text-slate-400">
                            We'll send you a personalized demo of your restaurant via email. This is just an overview - if you're interested, we'll reach out to discuss your specific needs personally.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="voiceflow"
                  className="w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {hasSlugParam && isValidSlug ? (
                    <div className="space-y-4">
                      {/* Welcome Banner */}
                      <motion.div 
                        className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-4 text-center"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <p className="text-yellow-400 font-medium">
                          🎉 Welcome to {restaurantData?.name || restaurantName}'s personalized demo!
                        </p>
                        <p className="text-slate-300 text-sm mt-1">
                          This chatbot has access to your restaurant's specific information.
                        </p>
                      </motion.div>
                      
                      {/* Voiceflow Container */}
                      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div 
                          ref={voiceflowRef}
                          id="vf-container-hero"
                          className="w-full"
                          style={{
                            minHeight: '600px',
                            height: 'auto'
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-800/90 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden p-12 text-center">
                      <motion.div 
                        className="w-24 h-24 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full flex items-center justify-center mx-auto mb-6"
                        animate={{
                          rotate: [0, 360],
                        }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <span className="text-4xl">🤖</span>
                      </motion.div>
                      <h3 className="text-2xl font-semibold text-white mb-4">
                        Ready to Experience Kairos AI?
                      </h3>
                      <p className="text-slate-300 mb-8 max-w-md mx-auto">
                        Click "Request Demo" to get a personalized demonstration of our AI assistant for your restaurant.
                      </p>
                      <div className="flex items-center justify-center space-x-4 text-sm text-slate-400">
                        <span>✨ Personalized Setup</span>
                        <span>•</span>
                        <span>🎯 Tailored to Your Brand</span>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default HeroNew;